import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

/** Guideline §04 — Button Components. */
export type ButtonVariant =
  | 'primary' // Structural Navy — the default commit action
  | 'secondary' // Digital Blue
  | 'outline'
  | 'positive' // Signal Green — approve, add
  | 'destructive'
  | 'ghost'
  | 'link';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-navy text-white hover:bg-navy-800 active:bg-midnight disabled:bg-navy/40 focus-visible:shadow-focus-navy',
  secondary:
    'bg-digital text-white hover:bg-digital-600 active:bg-digital-700 disabled:bg-digital/40 focus-visible:shadow-focus-navy',
  outline:
    'border border-line bg-white text-body hover:bg-cloud hover:border-navy-200 active:bg-pale-blue disabled:text-muted/60',
  positive:
    'bg-signal text-white hover:bg-signal-600 active:bg-signal-700 disabled:bg-signal/40 focus-visible:shadow-focus-signal',
  destructive:
    'bg-danger text-white hover:bg-danger-600 active:bg-danger-700 disabled:bg-danger/40 focus-visible:shadow-focus-danger',
  ghost: 'text-body hover:bg-cloud active:bg-pale-blue disabled:text-muted/60',
  link: 'text-digital underline-offset-4 hover:underline disabled:text-muted/60 px-0',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 rounded px-3 text-[13px]',
  md: 'h-10 gap-2 rounded px-4 text-sm',
  lg: 'h-12 gap-2 rounded-lg px-5 text-[15px]',
  icon: 'h-9 w-9 rounded',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Rendered before the label. Hidden while loading, so the button never jumps width. */
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    leadingIcon,
    trailingIcon,
    fullWidth,
    className,
    children,
    disabled,
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex select-none items-center justify-center whitespace-nowrap font-semibold transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-0',
        'disabled:cursor-not-allowed',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? (
        <Loader2 aria-hidden className="h-4 w-4 shrink-0 animate-spin" />
      ) : (
        leadingIcon && <span className="shrink-0 [&>svg]:h-4 [&>svg]:w-4">{leadingIcon}</span>
      )}
      {children}
      {!loading && trailingIcon && <span className="shrink-0 [&>svg]:h-4 [&>svg]:w-4">{trailingIcon}</span>}
    </button>
  );
});
